import { useState, useRef, useEffect } from 'react'
import type { KeyboardEvent, ReactNode } from 'react'
import { env } from '@/config/env.ts'

// ─── Backend chat proxy ──────────────────────────────────────────────────────
// BL-C-02: the Groq API key is no longer shipped to the browser. The system
// prompt + Groq call live in `api/chat.ts`. This component only POSTs the
// conversation history and consumes the SSE stream.
function chatEndpoint(): string {
  const base = env.notificationApiBase?.trim() || ''
  return `${base}/api/chat`
}

// ─── Types ────────────────────────────────────────────────────────────────────
type Role = 'user' | 'assistant'
interface Message {
  role: Role
  content: string
}
interface GroqResponse {
  choices?: Array<{ message: { role: string; content: string } }>
}

// ─── SVG lock icon — replaces emoji for reliable cross-env rendering ──────────
function LockIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect
        x="3"
        y="11"
        width="18"
        height="11"
        rx="2"
        ry="2"
        fill="rgba(255,255,255,0.15)"
        stroke="#fff"
      />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  )
}

// The system prompt now lives in `api/chat.ts` (server-side) so it cannot be
// overridden by a malicious client. See BL-C-02 in docs/SECURITY_AUDIT_FIXES.md.

// ─── Quick suggestion chips ───────────────────────────────────────────────────
const SUGGESTIONS: string[] = [
  'How do I create a vault?',
  'How do I check in?',
  'How do I add a recipient?',
  'What happens when the switch triggers?',
  'How do I upload files?',
  'Is my data encrypted?',
]

// ─── Markdown renderer ────────────────────────────────────────────────────────
function inlineFormat(text: string): ReactNode[] {
  return text.split(/(\*\*.*?\*\*|`.*?`)/g).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**'))
      return <strong key={i}>{part.slice(2, -2)}</strong>
    if (part.startsWith('`') && part.endsWith('`'))
      return (
        <code
          key={i}
          style={{
            background: 'rgba(255,255,255,0.12)',
            borderRadius: 4,
            padding: '1px 5px',
            fontSize: 12,
            fontFamily: 'monospace',
          }}
        >
          {part.slice(1, -1)}
        </code>
      )
    return part
  })
}

function renderMarkdown(text: string): ReactNode[] {
  // FIX: normalise escaped \n sequences produced in some production environments
  const normalised = text.replace(/\\n/g, '\n')
  const lines = normalised.split('\n')
  const elements: ReactNode[] = []
  let listItems: string[] = []
  let listType: 'ol' | 'ul' | null = null

  const flushList = (key: string | number): void => {
    if (!listItems.length) return
    const Tag = listType === 'ol' ? 'ol' : 'ul'
    elements.push(
      <Tag key={`list-${key}`} style={{ paddingLeft: 18, margin: '4px 0' }}>
        {listItems.map((li, i) => (
          <li key={i}>{inlineFormat(li)}</li>
        ))}
      </Tag>,
    )
    listItems = []
    listType = null
  }

  lines.forEach((line, i) => {
    const olMatch = line.match(/^\d+\.\s+(.*)/)
    const ulMatch = line.match(/^[-*]\s+(.*)/)
    if (olMatch) {
      if (listType !== 'ol') flushList(i)
      listType = 'ol'
      listItems.push(olMatch[1])
      return
    }
    if (ulMatch) {
      if (listType !== 'ul') flushList(i)
      listType = 'ul'
      listItems.push(ulMatch[1])
      return
    }
    flushList(i)
    if (line.trim() === '') {
      elements.push(<br key={i} />)
      return
    }
    elements.push(
      <p key={i} style={{ margin: '2px 0' }}>
        {inlineFormat(line)}
      </p>,
    )
  })
  flushList('end')
  return elements
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function DeadmanChatbot() {
  const [open, setOpen] = useState<boolean>(false)
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content:
        "Hey! I'm Alex, your Bitslegacy support specialist. Whether you're just getting started or need help with something specific — I'm here. What can I help you with?",
    },
  ])
  const [input, setInput] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [unread, setUnread] = useState<number>(0)
  const bottomRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading])

  useEffect(() => {
    if (open) {
      setUnread(0)
      inputRef.current?.focus()
    }
  }, [open])

  const sendMessage = async (text?: string): Promise<void> => {
    const userText = (text ?? input).trim()
    if (!userText || loading) return
    setInput('')

    const userMessage: Message = { role: 'user', content: userText }
    const assistantMessage: Message = { role: 'assistant', content: '' }
    const chatHistory: Message[] = [...messages, userMessage]
    const nextMessages: Message[] = [...messages, userMessage, assistantMessage]
    setMessages(nextMessages)
    const assistantIndex = nextMessages.length - 1
    setLoading(true)

    try {
      const res = await fetch(chatEndpoint(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: chatHistory.map((m) => ({
            role: m.role,
            content: m.content,
          })),
        }),
      })

      if (!res.ok) {
        let detail = ''
        try {
          const j = (await res.json()) as { error?: string }
          detail = j.error ?? ''
        } catch {
          detail = await res.text().catch(() => '')
        }
        throw new Error(
          detail || `Chat service error (${res.status}). Please try again.`,
        )
      }

      // Fallback for browsers without streaming support
      if (!res.body) {
        const data: GroqResponse = await res.json()
        const reply =
          data.choices?.[0]?.message?.content ??
          "Sorry, I couldn't get a response."
        setMessages((prev) => {
          const copy = [...prev]
          copy[assistantIndex] = { ...copy[assistantIndex], content: reply }
          return copy
        })
        if (!open) setUnread((n) => n + 1)
        return
      }

      const reader = res.body.getReader()
      const decoder = new TextDecoder('utf-8')
      let buffer = ''
      let assistantText = ''
      let streamDone = false

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })

        const lines = buffer.split('\n')
        buffer = lines.pop() ?? ''

        for (const line of lines) {
          const trimmed = line.trim()
          if (!trimmed.startsWith('data:')) continue

          const payload = trimmed.slice('data:'.length).trim()
          if (payload === '[DONE]') {
            reader.cancel().catch(() => {})
            streamDone = true
            break
          }

          try {
            const json = JSON.parse(payload) as {
              choices?: Array<{ delta?: { content?: string } }>
            }
            const delta = json.choices?.[0]?.delta?.content
            if (!delta) continue

            assistantText += delta
            setMessages((prev) => {
              const copy = [...prev]
              const current = copy[assistantIndex]
              if (!current) return prev
              copy[assistantIndex] = { ...current, content: assistantText }
              return copy
            })
          } catch {
            // Ignore malformed/partial lines
          }
        }

        if (streamDone) break
      }

      if (!open) setUnread((n) => n + 1)
    } catch (e) {
      const msg =
        e instanceof Error
          ? e.message
          : "⚠️ Couldn't reach the assistant right now. Check your API key or internet connection."

      setMessages((prev) => [...prev, { role: 'assistant', content: msg }])
    } finally {
      setLoading(false)
    }
  }

  const handleKey = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void sendMessage()
    }
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600&display=swap');
        @keyframes slideUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
        @keyframes bounce  { 0%,80%,100%{transform:translateY(0);opacity:.4} 40%{transform:translateY(-5px);opacity:1} }
        .dm-typing-dot { width:6px;height:6px;border-radius:50%;background:var(--color-brand);animation:bounce 1.2s infinite;display:inline-block; }
        .dm-typing-dot:nth-child(2){animation-delay:.2s}
        .dm-typing-dot:nth-child(3){animation-delay:.4s}
        .dm-panel { animation: slideUp 0.22s ease; }
        .dm-sug-btn:hover { border-color:var(--color-brand) !important; color:var(--color-primary-hover) !important; }
        .dm-send:disabled { opacity:.4; cursor:default; }
        .dm-messages::-webkit-scrollbar { width:3px; }
        .dm-messages::-webkit-scrollbar-thumb { background:var(--color-border-focus); border-radius:3px; }
        .dm-textarea:focus { border-color:var(--color-brand) !important; }
      `}</style>

      {/* Launcher button */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="fixed right-6 bottom-6 z-[9999] flex h-[56px] w-[56px] cursor-pointer items-center justify-center rounded-full border-none shadow-[var(--shadow-glow-lg)] transition-transform duration-200 outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-bg-primary)]"
        style={{ background: 'var(--gradient-brand)' }}
        title="Open help chat"
      >
        <span className="relative">
          {open ? (
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          ) : (
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          )}
          {!open && unread > 0 && (
            <span className="absolute -top-[4px] -right-[4px] flex h-[18px] w-[18px] items-center justify-center rounded-full bg-[var(--color-error)] text-[10px] font-bold text-white">
              {unread}
            </span>
          )}
        </span>
      </button>

      {/* Chat panel */}
      {open && (
        <div
          className="dm-panel fixed right-6 bottom-[90px] z-[9998] flex h-[540px] w-[370px] flex-col overflow-hidden rounded-[18px] border border-[var(--color-border)] bg-[var(--color-bg-secondary)] shadow-[var(--shadow-lg)]"
          style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}
        >
          {/* Header */}
          <div className="flex flex-shrink-0 items-center gap-2.5 border-b border-[var(--color-border)] bg-[var(--color-bg-tertiary)] px-4 py-3">
            {/* FIX: SVG icon instead of emoji — emoji renders as "Al" in some Vercel/SSR builds */}
            <div
              className="flex h-[34px] w-[34px] flex-shrink-0 items-center justify-center rounded-full"
              style={{ background: 'var(--gradient-brand)' }}
            >
              <LockIcon size={18} />
            </div>
            <div>
              <div className="text-[13px] leading-[1.2] font-semibold text-[var(--color-text-primary)]">
                Alex · Bitslegacy Support
              </div>
              <div className="flex items-center gap-1 text-[11px] text-[var(--color-secondary)]">
                <span className="h-[6px] w-[6px] rounded-full bg-[var(--color-secondary)]" />
                Online
              </div>
            </div>
            <button
              className="ml-auto cursor-pointer border-none bg-transparent p-1 text-[20px] leading-none text-[var(--color-text-secondary)]"
              onClick={() => setOpen(false)}
              type="button"
            >
              ×
            </button>
          </div>

          {/* Messages */}
          <div className="dm-messages flex flex-1 flex-col gap-[10px] overflow-y-auto p-[14px]">
            {messages.map((m, i) =>
              m.role === 'assistant' ? (
                <div key={i} className="flex max-w-[88%] gap-[7px] self-start">
                  {/* FIX: SVG icon instead of emoji */}
                  <div
                    className="mt-[2px] flex h-[26px] w-[26px] flex-shrink-0 items-center justify-center rounded-full"
                    style={{ background: 'var(--gradient-brand)' }}
                  >
                    <LockIcon size={13} />
                  </div>
                  <div className="rounded-[14px_14px_14px_4px] bg-[var(--color-bg-card)] px-[13px] py-[9px] text-[13px] leading-[1.55] text-[var(--color-text-primary)]">
                    {renderMarkdown(m.content)}
                  </div>
                </div>
              ) : (
                <div
                  key={i}
                  className="flex max-w-[88%] flex-row-reverse items-end gap-[7px] self-end"
                >
                  <div className="mt-[2px] flex h-[26px] w-[26px] flex-shrink-0 items-center justify-center rounded-full bg-[var(--color-bg-card)] text-[10px] text-[var(--color-text-secondary)]">
                    You
                  </div>
                  <div
                    className="rounded-[14px_14px_4px_14px] px-[13px] py-[9px] text-[13px] leading-[1.55] text-[var(--color-text-inverse)]"
                    style={{ background: 'var(--gradient-brand)' }}
                  >
                    {m.content}
                  </div>
                </div>
              ),
            )}

            {/* Only show typing dots before the first token arrives */}
            {loading && messages[messages.length - 1]?.content === '' && (
              <div className="flex max-w-[88%] gap-[7px] self-start">
                {/* FIX: SVG icon instead of emoji */}
                <div
                  className="mt-[2px] flex h-[26px] w-[26px] flex-shrink-0 items-center justify-center rounded-full"
                  style={{ background: 'var(--gradient-brand)' }}
                >
                  <LockIcon size={13} />
                </div>
                <div className="flex items-center gap-[4px] rounded-[14px_14px_14px_4px] bg-[var(--color-bg-card)] px-[14px] py-[10px]">
                  <span className="dm-typing-dot" />
                  <span className="dm-typing-dot" />
                  <span className="dm-typing-dot" />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Suggestion chips */}
          {messages.length <= 1 && (
            <div className="flex flex-shrink-0 flex-wrap gap-[5px] px-[12px] pb-[8px]">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  className="dm-sug-btn cursor-pointer rounded-full border border-[var(--color-border)] bg-transparent px-[11px] py-[4px] text-[11px] whitespace-nowrap text-[var(--color-text-secondary)]"
                  onClick={() => void sendMessage(s)}
                  type="button"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input area */}
          <div className="flex flex-shrink-0 items-end gap-[8px] border-t border-[var(--color-border)] px-[12px] py-[10px]">
            <textarea
              ref={inputRef}
              rows={1}
              placeholder="Ask anything…"
              className="dm-textarea max-h-[80px] flex-1 resize-none rounded-[10px] border border-[var(--color-border)] bg-[var(--color-bg-card)] px-[12px] py-[8px] text-[13px] leading-[1.5] text-[var(--color-text-primary)] outline-none"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
            />
            <button
              className="dm-send flex h-[34px] w-[34px] flex-shrink-0 cursor-pointer items-center justify-center rounded-full border-none outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-bg-secondary)]"
              style={{ background: 'var(--gradient-brand)' }}
              onClick={() => void sendMessage()}
              disabled={!input.trim() || loading}
              title="Send"
              type="button"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#fff"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  )
}
